import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosGoalsService } from '@/services/roros/goals.service';
import { rorosKeys } from './keys';
import type { CreateGoalPayload, UpdateGoalPayload } from '@/types/roros/goals';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Shared query key base ────────────────────────────────────────────────────

const goalsBase = ['roros', 'goals'] as const;

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useRorosGoals(params?: {
  status?: string;
  domainId?: string;  // A1: camelCase on API surface
  parent_type?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: [...goalsBase, params] as const,
    queryFn: () => RorosGoalsService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosGoal(id: string) {
  return useQuery({
    queryKey: [...goalsBase, id] as const,
    queryFn: () => RorosGoalsService.getById(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────────

export function useRorosGoalCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGoalPayload) => RorosGoalsService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalsBase });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

export function useRorosGoalUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateGoalPayload }) =>
      RorosGoalsService.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: goalsBase });
      qc.invalidateQueries({ queryKey: [...goalsBase, id] });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}

export function useRorosGoalArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosGoalsService.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalsBase });
      qc.invalidateQueries({ queryKey: rorosKeys.tree.all });
    },
  });
}
